export const ARTICLE_FILTER_GROUPS = {
  roomTypeFilter: 'roomTypes',
  solutionTypeFilter: 'solutionTypes',
  windowTreatmentFilter: 'windowTreatments',
  categoryFilter: 'articleTypes',
};

export const BLOG_FILTER_QUERY_GROUPS = {
  roomTypeFilter: 'room',
  solutionTypeFilter: 'solution',
  windowTreatmentFilter: 'treatment',
  categoryFilter: 'contenttype',
};

export const buildBlogFilterQuery = (filters, selectedFilters, searchText) => {
  const params = new URLSearchParams();

  Object.keys(BLOG_FILTER_QUERY_GROUPS).forEach((filterKey) => {
    const filter = filters && filters[filterKey];
    const selected = selectedFilters && selectedFilters[filterKey];

    if (!filter || !Array.isArray(filter.options) || !Array.isArray(selected)) {
      return;
    }

    const selectedIds = filter.options
      .filter(option => selected.includes(option.name))
      .map(option => String(option.id))
      .filter((id, index, ids) => id && ids.indexOf(id) === index);

    if (selectedIds.length > 0) {
      params.set(BLOG_FILTER_QUERY_GROUPS[filterKey], selectedIds.join(','));
    }
  });

  const normalizedSearch = (searchText || '').trim();
  if (normalizedSearch) {
    params.set('q', normalizedSearch);
  }

  return params.toString();
};

export const restoreBlogFilterState = (filters, search) => {
  const params = new URLSearchParams(search || '');
  const restoredFilters = {};
  const selectedFilters = {
    roomTypeFilter: [],
    solutionTypeFilter: [],
    windowTreatmentFilter: [],
    categoryFilter: [],
  };

  Object.keys(filters || {}).forEach((filterKey) => {
    const filter = filters[filterKey];

    if (!filter || typeof filter === 'string') {
      restoredFilters[filterKey] = filter;
      return;
    }

    const queryKey = BLOG_FILTER_QUERY_GROUPS[filterKey];
    const selectedIds = new Set(queryKey
      ? (params.get(queryKey) || '').split(',').map(id => id.trim()).filter(Boolean)
      : []);

    const options = (filter.options || []).map((option) => {
      const isChecked = selectedIds.has(String(option.id));
      if (isChecked) {
        selectedFilters[filterKey].push(option.name);
      }

      return Object.assign({}, option, { isChecked });
    });

    restoredFilters[filterKey] = Object.assign({}, filter, {
      active: false,
      options,
    });
  });

  return {
    filters: restoredFilters,
    selectedFilters,
    searchText: (params.get('q') || '').trim(),
  };
};

export const appendQueryToUrl = (url, query) => {
  if (!url || !query) {
    return url;
  }

  const hashIndex = url.indexOf('#');
  const hash = hashIndex >= 0 ? url.slice(hashIndex) : '';
  const baseUrl = hashIndex >= 0 ? url.slice(0, hashIndex) : url;
  const separator = baseUrl.includes('?') ? '&' : '?';

  return `${baseUrl}${separator}${query}${hash}`;
};

export const matchesCategoryGroup = (articleCategories, selectedFilters) => {
  if (!selectedFilters || selectedFilters.length === 0) {
    return true;
  }

  if (!articleCategories || articleCategories.length === 0) {
    return false;
  }

  const categoryNames = articleCategories
    .map(category => (category && category.name ? String(category.name).toLowerCase() : ''))
    .filter(Boolean);

  return selectedFilters.some(filter => (
    filter && categoryNames.includes(String(filter).toLowerCase())
  ));
};

export const stripHtml = (value) => {
  if (!value) {
    return '';
  }

  return String(value)
    .replace(/<(script|style)[^>]*>[\s\S]*?<\/\1>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\s+/g, ' ')
    .trim();
};

export const matchesArticleSearch = (article, searchText) => {
  const term = (searchText || '').trim().toLowerCase();

  if (!term) {
    return true;
  }

  if (!article) {
    return false;
  }

  const haystack = [
    article.articleTitle,
    article.articleSubheading,
    stripHtml(article.bodyCopy),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return haystack.includes(term);
};

export const sortArticlesByDate = articleItems => (
  [...articleItems].sort((a, b) => new Date(b.publishDate) - new Date(a.publishDate))
);

export const cloneSelectedFilters = (selectedFilters = {}) => ({
  roomTypeFilter: [...(selectedFilters.roomTypeFilter || [])],
  solutionTypeFilter: [...(selectedFilters.solutionTypeFilter || [])],
  windowTreatmentFilter: [...(selectedFilters.windowTreatmentFilter || [])],
  categoryFilter: [...(selectedFilters.categoryFilter || [])],
});

export const buildPendingFilters = (filters, selectedFilters) => {
  const pendingFilters = {};

  Object.keys(filters).forEach((filterKey) => {
    if (typeof filters[filterKey] === 'string') {
      pendingFilters[filterKey] = filters[filterKey];
      return;
    }

    const filter = filters[filterKey];

    pendingFilters[filterKey] = Object.assign({}, filter, {
      active: false,
      options: (filter.options || []).map(option => Object.assign({}, option, {
        isChecked: (selectedFilters[filterKey] || []).includes(option.name),
      })),
    });
  });

  return pendingFilters;
};

export const togglePendingFilterSection = (pendingFilters, filterName) => {
  const newFilters = Object.assign({}, pendingFilters);

  Object.keys(newFilters).forEach((filterKey) => {
    if (typeof newFilters[filterKey] !== 'string') {
      const isTarget = newFilters[filterKey].name === filterName;

      newFilters[filterKey] = Object.assign({}, newFilters[filterKey], {
        active: isTarget ? !newFilters[filterKey].active : false,
      });
    }
  });

  return newFilters;
};

export const togglePendingFilterOption = (pendingFilters, option) => {
  const newFilters = Object.assign({}, pendingFilters);

  Object.keys(newFilters).forEach((filterKey) => {
    if (typeof newFilters[filterKey] !== 'string') {
      newFilters[filterKey] = Object.assign({}, newFilters[filterKey], {
        options: newFilters[filterKey].options.map((optionItem) => {
          if (option.type === 'checkbox' && option.name === optionItem.name) {
            return Object.assign({}, optionItem, {
              isChecked: !optionItem.isChecked,
            });
          }

          return optionItem;
        }),
      });
    }
  });

  return newFilters;
};

export const extractSelectedFiltersFromPending = (pendingFilters) => {
  const selectedFilters = cloneSelectedFilters();

  Object.keys(pendingFilters).forEach((filterKey) => {
    if (typeof pendingFilters[filterKey] !== 'string') {
      selectedFilters[filterKey] = pendingFilters[filterKey].options
        .filter(option => option.isChecked === true)
        .map(option => option.name);
    }
  });

  return selectedFilters;
};
